import { SESSION_ENDED_CLOSE, type ClientFrame, type ServerFrame } from "@astrum/shared";
import { socketUrl } from "./transport.js";

/**
 * The realtime client.
 *
 * A plain class rather than a hook so the connection survives React remounts:
 * StrictMode double-mounts in development, and a socket owned by a component
 * would be torn down and rebuilt on every navigation. One socket is created
 * for the session and shared by whatever is on screen.
 *
 * Reconnection is the whole point of this file. Networks drop, laptops sleep,
 * and servers restart; a chat client that needs a page refresh after any of
 * those is not finished. Nothing is replayed over the socket after a drop:
 * whoever is on screen refetches over HTTP when the status returns to "open"
 * (see `useReconnected`).
 */

/** `unauthenticated` is terminal: there is no session, so no amount of
 *  reconnecting will help and the UI should send the user to sign in. */
export type RealtimeStatus = "connecting" | "open" | "reconnecting" | "closed" | "unauthenticated";

type Listener = (frame: ServerFrame) => void;
type StatusListener = (status: RealtimeStatus) => void;

/** Backoff schedule, in ms. Caps at 30s rather than growing without bound: a
 * server that comes back after ten minutes should be noticed promptly. */
const BACKOFF_MS = [500, 1_000, 2_000, 5_000, 10_000, 20_000, 30_000];

export class RealtimeClient {
  private socket: WebSocket | undefined;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private reconnectListeners = new Set<() => void>();
  /** Whether any socket this session has reached `ready`, so the next `ready`
   *  is known to follow a drop. Cleared on disconnect (sign-out). */
  private hasBeenReady = false;
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private status: RealtimeStatus = "closed";
  private stopped = true;
  private focusedChannelId: string | null = null;

  connect(): void {
    this.stopped = false;
    void this.open();
  }

  private async open(): Promise<void> {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.setStatus(this.attempt === 0 ? "connecting" : "reconnecting");

    // The session cookie travels with the upgrade on its own, in the browser
    // and the desktop app alike.
    const socket = new WebSocket(socketUrl());
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.setStatus("open");

      // Re-announce focus: the server holds this per connection, so a new
      // socket starts with none and would otherwise drop typing indicators.
      if (this.focusedChannelId) {
        this.send({ t: "focus", channelId: this.focusedChannelId });
      }
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      let frame: ServerFrame;
      try {
        frame = JSON.parse(event.data) as ServerFrame;
      } catch {
        return;
      }

      // The server pings by sending `pong`; answering keeps the connection
      // from being reaped as half-open.
      if (frame.t === "pong") {
        this.send({ t: "ping" });
        return;
      }

      for (const listener of this.listeners) listener(frame);

      /*
       * `ready` rather than `onopen` marks the reconnect: the server sends it
       * only once this socket is subscribed to every channel, so a refetch
       * started now cannot miss a message posted in the gap — anything newer
       * than the fetch arrives over the socket.
       */
      if (frame.t === "ready") {
        if (this.hasBeenReady) for (const listener of this.reconnectListeners) listener();
        this.hasBeenReady = true;
      }
    };

    socket.onclose = (event: CloseEvent) => {
      this.socket = undefined;
      if (this.stopped) {
        this.setStatus("closed");
        return;
      }
      // No session behind this socket — it ended, or was never there. Every
      // retry would be refused the same way, and used to be, forever: the app
      // looked signed in while receiving nothing. Stop and say so; the shell
      // sends the person to sign in.
      if (event.code === SESSION_ENDED_CLOSE) {
        this.stopped = true;
        this.setStatus("unauthenticated");
        return;
      }
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      // `onclose` always follows, so reconnection is handled in one place.
      socket.close();
    };
  }

  private scheduleReconnect(): void {
    this.setStatus("reconnecting");
    const delay = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)]!;
    this.attempt += 1;

    clearTimeout(this.reconnectTimer);
    // Jitter avoids every client in the workspace reconnecting on the same
    // tick after a server restart and immediately knocking it over again.
    this.reconnectTimer = setTimeout(() => void this.open(), delay + Math.random() * 400);
  }

  disconnect(): void {
    this.stopped = true;
    this.hasBeenReady = false;
    clearTimeout(this.reconnectTimer);
    this.socket?.close(1000, "Client closed");
    this.socket = undefined;
    this.setStatus("closed");
  }

  send(frame: ClientFrame): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(frame));
  }

  setFocus(channelId: string | null): void {
    this.focusedChannelId = channelId;
    this.send({ t: "focus", channelId });
  }

  notifyTyping(channelId: string): void {
    this.send({ t: "typing", channelId });
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Called each time a socket is ready again after a drop — never on the first
   * connect. The moment to refetch whatever is on screen, since frames sent
   * while disconnected are gone.
   */
  onReconnect(listener: () => void): () => void {
    this.reconnectListeners.add(listener);
    return () => this.reconnectListeners.delete(listener);
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  getStatus(): RealtimeStatus {
    return this.status;
  }

  private setStatus(status: RealtimeStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const listener of this.statusListeners) listener(status);
  }
}

export const realtime = new RealtimeClient();
